import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Send, Trash2, RotateCcw, Bell, BellOff, Clock3, CheckCircle2, Eye, UserPlus, Sparkles, Monitor, Smartphone, X } from "lucide-react";
import api from "@/lib/api";
import ShareButtons from "@/components/ShareButtons";
import { getTheme } from "@/lib/eventTheme";
import { AIMessageStudio, AIDraftLabel } from "@/components/AICopilot";

const STATUS_META = {
  invited: { label: "Invited", icon: Mail, color: "24 10% 45%", bg: "34 22% 93%" },
  opened: { label: "Opened", icon: Eye, color: "196 70% 28%", bg: "196 55% 92%" },
  recorded: { label: "Recorded", icon: CheckCircle2, color: "152 40% 26%", bg: "152 30% 90%" },
};

function parseInvites(raw) {
  const emailRe = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
  const rows = [];
  raw.split(/\n/).forEach((line) => {
    const l = line.trim();
    if (!l) return;
    const em = l.match(emailRe);
    const email = em ? em[1] : null;
    let name = l.replace(emailRe, "").replace(/[<>,;|]/g, " ").trim();
    if (!name && email) name = email.split("@")[0];
    rows.push({ name, email });
  });
  return rows;
}

export default function InvitationsPanel({ ev }) {
  const eventId = ev.event_id;
  const theme = getTheme(ev.type);
  const url = `${window.location.origin}/t/${ev.slug}`;
  const [data, setData] = useState(null);
  const [raw, setRaw] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [reminders, setReminders] = useState(true);
  const [showStudio, setShowStudio] = useState(false);
  const [noteFromAI, setNoteFromAI] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null); // { html, subject, sender, brand_applied }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewViewport, setPreviewViewport] = useState("desktop");

  const openPreview = async () => {
    setShowPreview(true);
    setPreviewLoading(true);
    try {
      const res = await api.post(`/events/${eventId}/invite-preview`, { personal_note: note, sample_name: "Priya" });
      setPreview(res.data);
    } catch {
      toast.error("Could not build the preview — try again in a moment.");
      setShowPreview(false);
    } finally { setPreviewLoading(false); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/events/${eventId}/invitations`);
      setData(res.data);
      setReminders(res.data.settings?.reminders_enabled !== false);
      if (res.data.settings?.invite_message && !note) setNote(res.data.settings.invite_message);
    } catch { toast.error("Could not load invitations"); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const invites = parseInvites(raw);
    if (invites.length === 0) { toast.error("Add at least one name or email"); return; }
    setSending(true);
    try {
      const res = await api.post(`/events/${eventId}/invitations`, { invites, personal_note: note || null, send: true });
      if (res.data.email_configured) toast.success(`Invitations sent to ${invites.filter((i) => i.email).length}`);
      else toast.message("Invitees added · email not configured yet", { description: "Add a Resend key to auto-send. Meanwhile, share the link directly below." });
      setRaw(""); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add invitations"); }
    finally { setSending(false); }
  };

  const resend = async (inv) => {
    try { const res = await api.post(`/events/${eventId}/invitations/${inv.invite_id}/resend`); res.data.email_configured ? toast.success("Reminder re-sent") : toast.message("Email not configured", { description: "Share the personal link instead." }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); }
  };
  const del = async (inv) => {
    try { await api.delete(`/events/${eventId}/invitations/${inv.invite_id}`); await load(); }
    catch { toast.error("Could not remove"); }
  };
  const toggleReminders = async () => {
    const next = !reminders; setReminders(next);
    try { await api.patch(`/events/${eventId}`, { reminders_enabled: next }); toast.success(next ? "Automated reminders on" : "Automated reminders off"); }
    catch { setReminders(!next); toast.error("Could not update"); }
  };
  const sendReminderNow = async () => {
    try { const res = await api.post(`/events/${eventId}/reminders/send-now`, { window: "3d" }); res.data.email_configured ? toast.success(`Reminder sent to ${res.data.sent}`) : toast.message("Email not configured", { description: "Add a Resend key to send reminders." }); }
    catch { toast.error("Could not send reminders"); }
  };

  if (!data) return <div className="py-10 grid place-items-center"><div className="h-8 w-8 rounded-full border-2 border-[hsl(var(--accent))] border-t-transparent animate-spin" /></div>;
  const c = data.counts;

  return (
    <div className="space-y-6">
      {/* summary */}
      <div className="grid grid-cols-3 gap-3">
        {[["invited", c.invited], ["opened", c.opened], ["recorded", c.recorded]].map(([k, v]) => {
          const m = STATUS_META[k];
          return (
            <div key={k} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: `hsl(${m.color})` }}><m.icon size={14} /> {m.label}</div>
              <div className="mt-1 font-display text-2xl font-semibold">{v}</div>
            </div>
          );
        })}
      </div>

      {/* add invitations */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
        <h3 className="font-display text-xl font-semibold">Invite people</h3>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Paste names & emails — one per line. e.g. <span className="font-mono">Aunt Ruth, ruth@email.com</span></p>
        <textarea data-testid="invite-import-input" value={raw} onChange={(e) => setRaw(e.target.value)} rows={4}
          placeholder={"Aunt Ruth, ruth@email.com\nJames Hale james@email.com\nsofia@email.com"}
          className="mt-3 w-full rounded-xl p-3 font-mono text-sm bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none" />
        <div className="flex items-center justify-between mt-4">
          <label className="block text-sm font-medium">A personal note (optional)</label>
          <button
            type="button"
            data-testid="invite-ai-studio-button"
            onClick={() => setShowStudio(true)}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[hsl(var(--accent))] hover:opacity-80"
          >
            <Sparkles size={14} /> Draft with AI
          </button>
        </div>
        {noteFromAI && <div className="mt-1"><AIDraftLabel /></div>}
        <textarea data-testid="invite-note-input" value={note} onChange={(e) => { setNote(e.target.value); if (noteFromAI) setNoteFromAI(false); }} rows={4}
          placeholder="Add a warm line that appears in the invitation email…"
          className="mt-1.5 w-full rounded-xl p-3 text-sm bg-[hsl(var(--background))] border border-[hsl(var(--input))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] resize-none" />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button data-testid="invite-preview-button" onClick={openPreview}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold text-sm hover:bg-[hsl(var(--muted))]">
            <Eye size={16} /> Preview email
          </button>
          <button data-testid="send-invitations-button" disabled={sending} onClick={send}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold disabled:opacity-60" style={{ background: `hsl(${theme.accent})` }}>
            <Send size={16} /> {sending ? "Sending…" : "Add & send invitations"}
          </button>
        </div>
        {!data.email_configured && <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Email isn&apos;t configured yet, so invitations won&apos;t auto-send. You can still share the link below and track opens/recordings.</p>}
      </div>

      {/* share + reminders */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
          <h3 className="font-semibold">Share the link</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 mb-3">A warm, ready-to-send message.</p>
          <ShareButtons type={ev.type} name={ev.honoree_name} url={url} accent={theme.accent} testidPrefix="event-share" />
        </div>
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
          <h3 className="font-semibold">Automated reminders</h3>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">We&apos;ll gently remind people who haven&apos;t recorded at 7 days, 3 days, and 24 hours before the deadline.</p>
          <div className="mt-3 flex items-center gap-3">
            <button data-testid="toggle-reminders-button" onClick={toggleReminders} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] font-semibold text-sm hover:bg-[hsl(var(--muted))]">
              {reminders ? <Bell size={16} /> : <BellOff size={16} />} {reminders ? "On" : "Off"}
            </button>
            <button data-testid="send-reminder-now-button" onClick={sendReminderNow} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-white" style={{ background: `hsl(${theme.accent})` }}><Clock3 size={16} /> Send a reminder now</button>
          </div>
        </div>
      </div>

      {/* invitee list */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
        <div className="p-4 border-b border-[hsl(var(--border))] font-semibold">Invitees ({c.total})</div>
        {data.invitations.length === 0 ? (
          <div className="p-8 text-center text-[hsl(var(--muted-foreground))] text-sm">No one invited yet. Add people above.</div>
        ) : (
          <div className="divide-y divide-[hsl(var(--border))]">
            {data.invitations.map((inv) => {
              const m = STATUS_META[inv.status] || STATUS_META.invited;
              const personalUrl = `${url}?i=${inv.invite_token}`;
              return (
                <div key={inv.invite_id} data-testid={`invitee-row-${inv.invite_id}`} className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="font-semibold text-sm">{inv.name || inv.email || "Guest"}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{inv.email || "no email · share link manually"}</div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: `hsl(${m.bg})`, color: `hsl(${m.color})` }}><m.icon size={13} /> {m.label}</span>
                  <div className="flex items-center gap-1">
                    <button title="Copy personal link" onClick={() => { navigator.clipboard.writeText(personalUrl); toast.success("Personal link copied"); }} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))]"><UserPlus size={16} /></button>
                    {inv.email && <button title="Resend" onClick={() => resend(inv)} className="h-9 w-9 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))]"><RotateCcw size={16} /></button>}
                    <button title="Remove" onClick={() => del(inv)} className="h-9 w-9 grid place-items-center rounded-lg text-[hsl(var(--destructive))] hover:bg-[hsl(var(--muted))]"><Trash2 size={16} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AIMessageStudio
        open={showStudio}
        onClose={() => setShowStudio(false)}
        eventId={eventId}
        onApply={(draft) => {
          const body = draft?.body || "";
          setNote(body);
          setNoteFromAI(true);
        }}
      />

      {/* Email preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-3 bg-black/60" onClick={() => setShowPreview(false)} data-testid="invite-preview-modal">
          <div className="w-full max-w-3xl max-h-[92vh] flex flex-col rounded-2xl bg-[hsl(var(--card))] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-[hsl(var(--border))]">
              <div className="h-9 w-9 grid place-items-center rounded-xl shrink-0" style={{ background: `hsl(${theme.accentSoft})`, color: `hsl(${theme.accent})` }}>
                <Eye size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-semibold text-sm truncate">Email preview</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                  {preview?.subject ? <><span className="font-semibold">Subject:</span> {preview.subject}</> : "Building preview…"}
                </div>
              </div>
              <div className="inline-flex rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                <button data-testid="preview-viewport-desktop" onClick={() => setPreviewViewport("desktop")}
                  className={`h-8 px-2.5 inline-flex items-center gap-1 text-xs font-semibold ${previewViewport === "desktop" ? "bg-[hsl(var(--muted))]" : ""}`}>
                  <Monitor size={13} /> Desktop
                </button>
                <button data-testid="preview-viewport-mobile" onClick={() => setPreviewViewport("mobile")}
                  className={`h-8 px-2.5 inline-flex items-center gap-1 text-xs font-semibold border-l border-[hsl(var(--border))] ${previewViewport === "mobile" ? "bg-[hsl(var(--muted))]" : ""}`}>
                  <Smartphone size={13} /> Mobile
                </button>
              </div>
              <button data-testid="preview-close" onClick={() => setShowPreview(false)} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto bg-[hsl(var(--muted))] p-4">
              {previewLoading ? (
                <div className="h-64 grid place-items-center text-[hsl(var(--muted-foreground))] text-sm">Building preview…</div>
              ) : preview ? (
                <div className="mx-auto" style={{ maxWidth: previewViewport === "mobile" ? 380 : 720 }}>
                  <iframe
                    data-testid="preview-iframe"
                    title="Email preview"
                    srcDoc={preview.html}
                    sandbox=""
                    className="w-full rounded-xl bg-white border border-[hsl(var(--border))]"
                    style={{ height: previewViewport === "mobile" ? "70vh" : "72vh" }}
                  />
                </div>
              ) : null}
            </div>

            <div className="px-5 py-3 border-t border-[hsl(var(--border))] flex items-center justify-between gap-2 text-xs text-[hsl(var(--muted-foreground))]">
              <div className="truncate">
                <span className="font-semibold text-[hsl(var(--foreground))]">From:</span> {preview?.sender || "—"}
                {preview?.brand_applied && <span className="ml-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-[hsl(var(--accent))]"><Sparkles size={11} /> Pro branding on</span>}
              </div>
              <button onClick={() => setShowPreview(false)} className="text-[hsl(var(--foreground))] font-semibold hover:underline">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
